<template>
    <div ref="managerRoot" tabindex="-1" class="library-history-manager acc-scrollbar min-h-0 flex-1 overflow-y-auto px-4 pb-5 outline-none sm:px-6">
        <section aria-labelledby="library-management-title" class="pt-5">
            <h3 id="library-management-title" class="text-sm font-extrabold">Керування книгою</h3>
            <div class="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label class="text-xs text-foxy-text-secondary"
                    >Статус
                    <select v-model="statusValue" data-profile-edit-initial class="acc-modal-input mt-1">
                        <option v-if="statusValue === null" :value="null" disabled>Не в поточній бібліотеці</option>
                        <option value="want_to_read">Хочу прочитати</option>
                        <option value="reading">Читаю</option>
                        <option value="read">Прочитано</option>
                        <option value="abandoned">Не дочитано</option>
                    </select>
                </label>
                <dl class="text-xs text-foxy-text-secondary">
                    <dt>Кількість прочитань</dt>
                    <dd class="mt-1 text-base font-semibold tabular-nums text-foxy-text">{{ form.times_read }}</dd>
                </dl>
            </div>
        </section>

        <section aria-labelledby="my-reading-history-title" class="mt-6">
            <h3 id="my-reading-history-title" ref="historyHeading" tabindex="-1" class="text-sm font-extrabold outline-none">Моя історія</h3>
            <div class="mt-3" aria-live="polite" aria-atomic="true">
                <div
                    v-if="historyState.status === 'loading'"
                    role="status"
                    class="flex min-h-28 items-center rounded-xl border border-foxy-border p-3 text-sm text-foxy-text-secondary"
                >
                    Завантажуємо історію читання…
                </div>
                <div
                    v-else-if="historyState.status === 'error'"
                    role="alert"
                    class="rounded-xl border border-foxy-error/30 p-3 text-sm text-foxy-error"
                >
                    <p>{{ historyState.error }}</p>
                    <button type="button" class="acc-btn mt-3 min-h-11 text-xs" @click="$emit('retry-history')">Спробувати ще раз</button>
                </div>
                <p v-else-if="historyState.status === 'loaded'" class="sr-only">Історію читання завантажено.</p>
            </div>

            <template v-if="historyState.status === 'loaded' && historyData">
                <div
                    v-if="readingMutationState.message"
                    ref="readingMessage"
                    tabindex="-1"
                    class="mt-4 rounded-xl border p-3 text-sm outline-none"
                    :class="
                        readingMutationState.messageType === 'success'
                            ? 'border-emerald-500/30 text-emerald-700 dark:text-emerald-300'
                            : 'border-foxy-error/30 text-foxy-error'
                    "
                    :role="readingMutationState.messageType === 'success' ? 'status' : 'alert'"
                >
                    {{ readingMutationState.message }}
                </div>

                <section v-if="historyData.current_reading" aria-labelledby="current-reading-title" class="mt-6">
                    <h3 id="current-reading-title" class="text-sm font-extrabold">Поточне читання</h3>
                    <library-reading-entry
                        ref="currentReadingEntry"
                        class="mt-3"
                        :reading="historyData.current_reading"
                        :busy="saving || readingSaving"
                        :language-label="languageLabel"
                        :show-unrated-rating-affordance="true"
                        @edit="openReadingEditor"
                    >
                        <section
                            v-if="isEditing(historyData.current_reading)"
                            :aria-labelledby="readingEditorTitleId(historyData.current_reading)"
                            class="mt-4 border-t border-foxy-border pt-4"
                        >
                            <h5 :id="readingEditorTitleId(historyData.current_reading)" class="text-sm font-extrabold text-foxy-text">
                                Редагування прочитання
                            </h5>
                            <library-reading-instance-editor
                                ref="activeReadingEditor"
                                :reading="historyData.current_reading"
                                :reading-status-lock-version="historyData.reading_status.lock_version"
                                :preferences-state="languagePreferencesState"
                                :format-options="historyData.format_options"
                                :saving="readingSaving"
                                :errors="readingMutationState.errors"
                                @dirty-change="activeReadingEditorDirty = $event"
                                @clear-errors="$emit('clear-reading-errors')"
                                @retry-preferences="$emit('retry-language-preferences')"
                                @save="forwardReadingSave"
                                @cancel="cancelReadingEdit"
                            />
                            <div class="mt-5 border-t border-foxy-error/25 pt-4">
                                <button
                                    type="button"
                                    class="acc-btn min-h-11 !border-foxy-error/40 !text-foxy-error"
                                    :disabled="saving || readingSaving"
                                    @click="forwardDelete(historyData.current_reading, $event)"
                                >
                                    Видалити прочитання
                                </button>
                            </div>
                        </section>
                    </library-reading-entry>
                </section>

                <div v-if="historyData.history.length || !historyData.current_reading" class="mt-6">
                    <div v-if="historyData.history.length" class="mt-3 space-y-3">
                        <library-reading-entry
                            v-for="reading in historyData.history"
                            :key="reading.id"
                            ref="historyReadingEntries"
                            class="break-words sm:flex-row"
                            :reading="reading"
                            :busy="saving || readingSaving"
                            :language-label="languageLabel"
                            @edit="openReadingEditor"
                        >
                            <section
                                v-if="isEditing(reading)"
                                :aria-labelledby="readingEditorTitleId(reading)"
                                class="mt-4 border-t border-foxy-border pt-4"
                            >
                                <h5 :id="readingEditorTitleId(reading)" class="text-sm font-extrabold text-foxy-text">Редагування прочитання</h5>
                                <library-reading-instance-editor
                                    ref="activeReadingEditor"
                                    :reading="reading"
                                    :reading-status-lock-version="historyData.reading_status.lock_version"
                                    :preferences-state="languagePreferencesState"
                                    :format-options="historyData.format_options"
                                    :saving="readingSaving"
                                    :errors="readingMutationState.errors"
                                    @dirty-change="activeReadingEditorDirty = $event"
                                    @clear-errors="$emit('clear-reading-errors')"
                                    @retry-preferences="$emit('retry-language-preferences')"
                                    @save="forwardReadingSave"
                                    @cancel="cancelReadingEdit"
                                />
                                <div class="mt-5 border-t border-foxy-error/25 pt-4">
                                    <button
                                        type="button"
                                        class="acc-btn min-h-11 !border-foxy-error/40 !text-foxy-error"
                                        :disabled="saving || readingSaving"
                                        @click="forwardDelete(reading, $event)"
                                    >
                                        Видалити прочитання
                                    </button>
                                </div>
                            </section>
                        </library-reading-entry>
                    </div>
                    <p v-else class="mt-2 text-sm text-foxy-text-secondary">Завершена історія читання з’явиться тут.</p>
                </div>

                <library-historical-reading-create-form
                    v-if="historicalCreateOpen"
                    ref="historicalCreateForm"
                    :language-options="historyData.language_options"
                    :language-preferences-state="languagePreferencesState"
                    :format-options="historyData.format_options"
                    :saving="historicalCreateSaving"
                    :errors="readingMutationState.errors"
                    @clear-errors="$emit('clear-reading-errors')"
                    @retry-language-preferences="$emit('retry-language-preferences')"
                    @cancel="cancelHistoricalCreate"
                    @save="$emit('create-historical-reading', $event)"
                />
                <button
                    v-else
                    ref="historicalCreateTrigger"
                    type="button"
                    class="acc-btn mt-4 min-h-11"
                    :disabled="saving || readingSaving"
                    @click="openHistoricalCreate"
                >
                    + Додати минуле прочитання
                </button>
                <library-archive-evidence :archive="historyData.archive" :language-label="languageLabel" />
            </template>
        </section>

        <section aria-labelledby="library-collections-title" class="mt-6 rounded-2xl border border-foxy-border bg-foxy-surface/60 p-4">
            <h3 id="library-collections-title" ref="collectionsHeading" tabindex="-1" class="text-sm font-extrabold outline-none">Добірки</h3>
            <div
                v-if="membershipConflict"
                class="mt-3 rounded-xl border border-foxy-warning/40 bg-foxy-warning/10 p-3 text-xs text-foxy-text"
                role="alert"
            >
                <p>{{ membershipConflict.message }}</p>
                <p v-if="membershipRecoveryError" class="mt-2 text-foxy-error">{{ membershipRecoveryError }}</p>
                <button type="button" class="acc-btn mt-3 min-h-11" :disabled="membershipRecoveryLoading" @click="$emit('recover-memberships')">
                    Оновити добірки
                </button>
            </div>
            <div v-if="membershipsLoading && !membershipConflict" class="mt-2 flex min-h-20 items-center text-xs text-foxy-text-secondary">
                Завантажуємо добірки…
            </div>
            <div v-else-if="membershipLoadFailed && !membershipConflict" class="mt-2 text-xs text-foxy-error">
                Не вдалося завантажити добірки.
                <button type="button" class="ml-1 underline" @click="$emit('retry-memberships')">Повторити</button>
            </div>
            <div v-else class="mt-2 space-y-1">
                <label v-for="library in userLibraries" :key="library.id" class="flex min-h-11 items-center gap-2 text-xs text-foxy-text-secondary">
                    <input v-model="libraryIdsValue" :value="library.id" type="checkbox" /><span>{{ library.name }}</span>
                </label>
            </div>
        </section>

        <section v-if="form.status !== null" aria-labelledby="library-danger-title" class="mt-6 border-t border-foxy-error/25 pt-5">
            <h3 id="library-danger-title" class="text-sm font-extrabold text-foxy-error">Небезпечна зона</h3>
            <button
                type="button"
                class="acc-btn mt-3 min-h-11 !border-foxy-error/40 !text-foxy-error"
                :disabled="saving"
                @click="$emit('remove-library')"
            >
                Прибрати з бібліотеки
            </button>
        </section>

        <div class="mt-6 flex flex-wrap justify-end gap-2 border-t border-foxy-border pt-5">
            <button type="button" class="acc-btn" :disabled="saving" @click="$emit('close')">Скасувати</button>
            <button type="button" class="acc-btn-primary" :disabled="saving || saveDisabled || historicalCreateOpen" @click="forwardManagerSave">
                {{ saving ? 'Збереження…' : 'Зберегти' }}
            </button>
        </div>
    </div>
</template>

<script lang="ts">
import LibraryArchiveEvidence from './LibraryArchiveEvidence.vue';
import LibraryReadingEntry from './LibraryReadingEntry.vue';
import LibraryReadingInstanceEditor from './LibraryReadingInstanceEditor.vue';
import LibraryHistoricalReadingCreateForm from './LibraryHistoricalReadingCreateForm.vue';

export default {
    name: 'LibraryReadingHistoryManager',
    components: { LibraryArchiveEvidence, LibraryHistoricalReadingCreateForm, LibraryReadingEntry, LibraryReadingInstanceEditor },
    props: {
        form: { type: Object, required: true },
        historyState: { type: Object, required: true },
        languageLabel: { type: Function, required: true },
        languagePreferencesState: {
            type: Object,
            default: () => ({ status: 'idle', data: null, error: '' }),
        },
        readingMutationState: {
            type: Object,
            default: () => ({ savingReadingId: null, errors: {}, message: '', messageType: '' }),
        },
        userLibraries: { type: Array, default: () => [] },
        membershipsLoading: { type: Boolean, default: false },
        membershipLoadFailed: { type: Boolean, default: false },
        membershipConflict: { type: Object, default: null },
        membershipRecoveryError: { type: String, default: '' },
        membershipRecoveryLoading: { type: Boolean, default: false },
        saveDisabled: { type: Boolean, default: false },
        saving: { type: Boolean, default: false },
        bookUrl: { type: String, default: '' },
        initialSection: { type: String, default: null },
        initialReadingInstanceId: { type: Number, default: null },
        openInitialReadingEditor: { type: Boolean, default: false },
    },
    data() {
        return {
            activeReadingEditorId: null,
            activeReadingEditorDirty: false,
            activeReadingEditorTrigger: null,
            historicalCreateOpen: false,
            initialReadingFocusApplied: null,
        };
    },
    computed: {
        historyData() {
            return this.historyState.data || null;
        },
        statusValue: {
            get() {
                return this.form.status;
            },
            set(value) {
                this.$emit('update-form', { ...this.form, status: value });
            },
        },
        libraryIdsValue: {
            get() {
                return this.form.library_ids;
            },
            set(value) {
                this.$emit('update-form', { ...this.form, library_ids: value });
            },
        },
        readingSaving() {
            return this.readingMutationState.savingReadingId !== null;
        },
        historicalCreateSaving() {
            return this.readingMutationState.savingReadingId === 'historical-create';
        },
    },
    watch: {
        'historyState.status': {
            immediate: true,
            handler(status) {
                if (status === 'loaded') this.focusInitialReading();
            },
        },
        initialReadingInstanceId() {
            this.initialReadingFocusApplied = null;
            if (this.historyState.status === 'loaded') this.focusInitialReading();
        },
    },
    methods: {
        focusInitialReading() {
            const readingId = Number(this.initialReadingInstanceId);
            if (!Number.isInteger(readingId) || readingId <= 0 || this.initialReadingFocusApplied === readingId) return;
            const readings = [this.historyData?.current_reading, ...(this.historyData?.history || [])].filter(Boolean);
            const reading = readings.find((item) => Number(item.id) === readingId);
            if (!reading) return;
            this.$nextTick(() => {
                const target = this.$el?.querySelector(`[data-reading-edit-id="${readingId}"]`);
                if (!target || target.disabled) return;
                target.scrollIntoView?.({ block: 'center' });
                this.initialReadingFocusApplied = readingId;
                if (this.openInitialReadingEditor) {
                    this.openReadingEditor(reading, { currentTarget: target });
                    return;
                }
                target.focus();
            });
        },
        isEditing(reading) {
            return Number(this.activeReadingEditorId) === Number(reading?.id);
        },
        readingEditorTitleId(reading) {
            return `reading-${reading.id}-editor-title`;
        },
        async openReadingEditor(reading, event = null) {
            if (!reading?.id || this.saving || this.readingSaving) return;
            if (this.historicalCreateOpen) this.cancelHistoricalCreate();
            if (this.activeReadingEditorId !== null && !this.isEditing(reading)) {
                const discarded = await this.requestDiscardActiveReadingEdit();
                if (!discarded) return;
            }
            if (this.isEditing(reading)) {
                this.focusActiveReadingEditor();
                return;
            }
            this.activeReadingEditorId = reading.id;
            this.activeReadingEditorDirty = false;
            this.activeReadingEditorTrigger = event?.currentTarget || null;
            this.$emit('clear-reading-errors');
            this.$nextTick(() => this.focusActiveReadingEditor());
        },
        async requestDiscardActiveReadingEdit() {
            if (this.activeReadingEditorId === null) return true;
            if (this.readingSaving) {
                this.focusActiveReadingEditor();
                return false;
            }
            if (this.activeReadingEditorDirty) {
                const confirmed = await window.confirm('Відхилити незбережені зміни цього прочитання?', 'Незбережені зміни', 'warning');
                if (!confirmed) {
                    this.focusActiveReadingEditor();
                    return false;
                }
            }
            this.activeReadingEditorRef()?.resetToCanonical();
            this.activeReadingEditorId = null;
            this.activeReadingEditorDirty = false;
            this.activeReadingEditorTrigger = null;
            this.$emit('clear-reading-errors');
            return true;
        },
        async cancelReadingEdit() {
            const readingId = this.activeReadingEditorId;
            const trigger = this.activeReadingEditorTrigger;
            const discarded = await this.requestDiscardActiveReadingEdit();
            if (!discarded) return;
            this.restoreReadingEditFocus(readingId, trigger);
        },
        forwardReadingSave(payload) {
            this.$emit('save-reading-instance', payload);
        },
        async openHistoricalCreate() {
            if (this.saving || this.readingSaving) return;
            if (this.activeReadingEditorId !== null && !(await this.requestDiscardActiveReadingEdit())) return;
            this.historicalCreateOpen = true;
            this.$emit('clear-reading-errors');
        },
        cancelHistoricalCreate() {
            if (this.historicalCreateSaving) return;
            this.historicalCreateOpen = false;
            this.$emit('clear-reading-errors');
            this.$nextTick(() => this.$refs.historicalCreateTrigger?.focus());
        },
        completeHistoricalCreate() {
            this.historicalCreateOpen = false;
            this.$nextTick(() => (this.$refs.historicalCreateTrigger || this.$refs.historyHeading)?.focus());
        },
        focusHistoricalCreateError(target = 'form') {
            this.$refs.historicalCreateForm?.focusError(target);
        },
        async forwardManagerSave() {
            if (this.historicalCreateOpen) return;
            if (this.activeReadingEditorId !== null) {
                const discarded = await this.requestDiscardActiveReadingEdit();
                if (!discarded) return;
            }
            this.$emit('save');
        },
        async forwardDelete(reading, event) {
            const fallbackTrigger = this.activeReadingEditorTrigger;
            if (this.activeReadingEditorId !== null && this.activeReadingEditorDirty) {
                const discarded = await this.requestDiscardActiveReadingEdit();
                if (!discarded) return;
                this.$emit('delete-reading', reading, fallbackTrigger ? { currentTarget: fallbackTrigger } : event);
                return;
            }
            this.$emit('delete-reading', reading, event);
        },
        completeReadingSave(readingId) {
            if (Number(this.activeReadingEditorId) !== Number(readingId)) return;
            const trigger = this.activeReadingEditorTrigger;
            this.activeReadingEditorId = null;
            this.activeReadingEditorDirty = false;
            this.activeReadingEditorTrigger = null;
            this.restoreReadingEditFocus(readingId, trigger);
        },
        closeReadingEditorAfterConflict(readingId) {
            this.completeReadingSave(readingId);
        },
        focusActiveReadingEditor() {
            this.$nextTick(() => this.activeReadingEditorRef()?.focusInitialControl());
        },
        focusReadingValidation(target) {
            this.$nextTick(() => this.activeReadingEditorRef()?.focusValidation(target));
        },
        activeReadingEditorRef() {
            const editor = this.$refs.activeReadingEditor;
            return Array.isArray(editor) ? editor[0] : editor;
        },
        restoreReadingEditFocus(readingId, trigger) {
            this.$nextTick(() => {
                if (trigger?.isConnected && !trigger.disabled) {
                    trigger.focus();
                    return;
                }
                const replacement = this.$el?.querySelector(`[data-reading-edit-id="${Number(readingId)}"]`);
                if (replacement && !replacement.disabled) replacement.focus();
                else this.focusHistory();
            });
        },
        focusHistory() {
            this.$nextTick(() => (this.$refs.historyHeading || this.$refs.managerRoot)?.focus());
        },
        focusCollections() {
            this.$nextTick(() => {
                this.$refs.collectionsHeading?.scrollIntoView?.({ block: 'start' });
                this.$refs.collectionsHeading?.focus();
            });
        },
    },
};
</script>
